import React, { useState } from "react";
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  InputAdornment,
  IconButton,
  Fade,
  Slide,
  Container,
  Divider,
  Alert
} from "@mui/material";
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  AdminPanelSettings,
  Security,
  Login as LoginIcon,
  ArrowBack
} from "@mui/icons-material";
import heroImage from "../../assets/Images/hero-8.jpg";
import { useNavigate } from "react-router-dom";
import { postData } from "../../services/Request";
import { toast } from 'react-toastify';

const AdminLoginPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email is invalid";
    }
    
    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setErrors({});

    const payload = {
      email: email,
      password: password,
    };
    
    try {
      const response = await postData("api/auth/login", payload);
      
      if (response.status === true && response.user.type === 'admin' && response.user.status == 1) {
        toast.success("Login successful!");
        // Save user data in localStorage
        localStorage.setItem("user", JSON.stringify(response.user));
        localStorage.setItem("token", response.token);
   
        // Redirect to Admin Dashboard
        navigate("/AdminDashbord");
      } else {
        toast.error(response.message || "Invalid email or password");
        setErrors({ general: response.message || "Invalid credentials" });
      }
    } catch (err) {
      toast.error("Login failed. Please try again.");
      setErrors({ general: "Login failed. Please check your credentials." });
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${heroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.1,
          zIndex: 0
        }
      }}
    >
      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Fade in timeout={1000}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                color: "white", 
                fontWeight: "bold", 
                mb: 1,
                textShadow: "2px 2px 4px rgba(0,0,0,0.3)"
              }}
            >
              Admin Portal
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ color: "rgba(255,255,255,0.8)" }}
            >
              Secure access to the DBS management system
            </Typography>
          </Box>
        </Fade>

        <Slide direction="up" in timeout={1200}>
          <Card sx={{
            borderRadius: 4,
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.2)"
          }}>
            <CardContent sx={{ p: 4 }}>
              {/* Header */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Avatar sx={{ 
                  bgcolor: "#667eea", 
                  width: 80, 
                  height: 80, 
                  mx: "auto", 
                  mb: 2,
                  boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)"
                }}>
                  <AdminPanelSettings sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                  Admin Login
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Enter your credentials to access the admin dashboard
                </Typography>
              </Box>

              {/* Error Alert */}
              {errors.general && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {errors.general}
                </Alert>
              )}

              {/* Login Form */}
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#667eea"
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#667eea"
                      }
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!!errors.password}
                  helperText={errors.password}
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#667eea"
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#667eea"
                      }
                    }
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    background: "linear-gradient(45deg, #667eea, #764ba2)",
                    boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
                    "&:hover": {
                      background: "linear-gradient(45deg, #5a6fd8, #6a4190)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 40px rgba(102, 126, 234, 0.4)"
                    },
                    "&:disabled": {
                      background: "rgba(0,0,0,0.1)",
                      color: "rgba(0,0,0,0.3)"
                    },
                    transition: "all 0.3s ease-in-out"
                  }}
                >
                  {loading ? "Signing In..." : "Sign In"}
                </Button>
              </Box>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Secure Access
                </Typography>
              </Divider>

              {/* Security Info */}
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                gap: 1,
                mt: 2
              }}>
                <Security color="action" fontSize="small" />
                <Typography variant="caption" color="text.secondary">
                  Your session is secured with enterprise-grade encryption
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Slide>

        {/* Back to Home Button */}
        <Fade in timeout={1500}>
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate("/")}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.1)"
                }
              }}
            >
              Back to Home
            </Button>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default AdminLoginPage;
